import React, { useMemo } from 'react';
import { Table, Button, Spinner } from 'react-bootstrap';
import { ISession } from './types';

interface SessionsTableProps {
  data: ISession[];
  onRevoke: (id: string) => void;
  onRevokeAll: (profileId: string) => void;
  loading: boolean;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('ru-RU');
  } catch {
    return dateStr;
  }
};

const SessionsTable: React.FC<SessionsTableProps> = ({
  data,
  onRevoke,
  onRevokeAll,
  loading
}) => {
  // Group sessions by profile_id to show "Revoke all" per operator
  const grouped = useMemo(() => {
    const map = new Map<
      string,
      { profileName: string; profileEmail: string; sessions: ISession[] }
    >();
    data.forEach(session => {
      const key = session.profile_id;
      if (!map.has(key)) {
        map.set(key, {
          profileName: session.profile_name,
          profileEmail: session.profile_email,
          sessions: []
        });
      }
      map.get(key)!.sessions.push(session);
    });
    return map;
  }, [data]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" /> Загрузка...
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        Нет активных сессий
      </div>
    );
  }

  return (
    <Table striped bordered hover responsive size="sm">
      <thead>
        <tr>
          <th>Оператор</th>
          <th>Email</th>
          <th>Создана</th>
          <th>Истекает</th>
          <th>Действия</th>
        </tr>
      </thead>
      <tbody>
        {Array.from(grouped.entries()).map(
          ([profileId, { profileName, profileEmail, sessions }]) => (
            <React.Fragment key={profileId}>
              {sessions.map((session, idx) => (
                <tr key={session.id}>
                  {idx === 0 ? (
                    <>
                      <td rowSpan={sessions.length}>
                        {profileName || profileId}
                      </td>
                      <td rowSpan={sessions.length}>{profileEmail || '—'}</td>
                    </>
                  ) : null}
                  <td>{formatDate(session.created_at)}</td>
                  <td>{formatDate(session.expires_at)}</td>
                  <td>
                    <Button
                      variant="danger"
                      size="sm"
                      className="me-1"
                      onClick={() => onRevoke(session.id)}
                      disabled={loading}
                    >
                      Отозвать
                    </Button>
                    {idx === 0 && sessions.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => onRevokeAll(profileId)}
                        disabled={loading}
                      >
                        Отозвать все
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          )
        )}
      </tbody>
    </Table>
  );
};

export default SessionsTable;
